<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

const API = '/chatbot-ai-api';
const DEFAULT_TEST_NUMBER = '5581999946376';
const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();
const loading = ref(false);
const saving = ref(false);
const action = ref('');
const configs = ref([]);
const selectedInboxId = ref(null);
const activeTab = ref('setup');
const apiKey = ref('');
const allowedNumbersText = ref('');
const blockedNumbersText = ref('');
const knowledgeTitle = ref('');
const knowledgeContent = ref('');
const stats = ref(null);
const providerTest = ref(null);
const providers = ref([]);
const providerModels = ref([]);
const modelsLoading = ref(false);
const modelsError = ref('');
const modelsFetchedAt = ref(null);
const n8nWorkflows = ref([]);
const n8nLoading = ref(false);
const savedSignature = ref('');
const pendingInboxId = ref(null);
const showDiscardDialog = ref(false);
const requestControllers = new Set();
let modelsGeneration = 0;
let statsGeneration = 0;

const defaultTools = () => [
  { name: 'consultar_cliente', description: 'Consulta cadastro e situação do cliente pelo telefone ou documento.', n8nWorkflowId: '', enabled: false, requiresApproval: false },
  { name: 'consultar_financeiro', description: 'Consulta vencimentos, faturas e situação financeira sem alterar dados.', n8nWorkflowId: '', enabled: false, requiresApproval: false },
  { name: 'gerar_pix', description: 'Gera ou recupera o PIX de uma cobrança após confirmação dos dados.', n8nWorkflowId: '', enabled: false, requiresApproval: true },
  { name: 'criar_teste', description: 'Cria um acesso de teste após coletar e confirmar os dados necessários.', n8nWorkflowId: '', enabled: false, requiresApproval: true },
  { name: 'enviar_tutorial', description: 'Localiza e retorna o tutorial adequado para a dúvida do cliente.', n8nWorkflowId: '', enabled: false, requiresApproval: false },
];

const draft = reactive({
  id: null,
  inboxId: null,
  inboxName: '',
  name: 'Assistente IA',
  mode: 'off',
  provider: 'openrouter',
  model: 'z-ai/glm-5.3-flash',
  fallbackModel: 'google/gemini-2.5-flash-lite',
  monthlyBudget: 15,
  maxReplyTokens: 260,
  temperature: 0.35,
  allowedNumbers: [DEFAULT_TEST_NUMBER],
  blockedNumbers: [],
  systemPrompt: 'Atenda dúvidas gerais com objetividade. Consulte as ferramentas antes de afirmar dados de clientes, cobranças ou acessos. Transfira para um atendente quando não tiver certeza.',
  styleProfile: '',
  tools: defaultTools(),
  connected: false,
  hasApiKey: false,
  trainedAt: null,
  trainingLimit: 500,
  trainingConversations: 0,
  n8nWorkflowId: null,
  n8nWorkflowUrl: null,
  n8nSyncedAt: null,
  n8nToolCount: 0,
});

const accountId = computed(() => Number(route.params.accountId));
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const selectedInbox = computed(() => inboxes.value.find(item => Number(item.id) === Number(selectedInboxId.value)));
const currentConfig = computed(() => configs.value.find(item => Number(item.inboxId) === Number(selectedInboxId.value)) || null);
const safeToConnect = computed(() => draft.id && (draft.hasApiKey || apiKey.value) && (draft.mode !== 'test' || parsedNumbers.value.length));
const parsedNumbers = computed(() => Array.from(new Set(allowedNumbersText.value.split(/[\s,;]+/).map(value => value.replace(/\D/g, '')).filter(value => value.length >= 10))));
const parsedBlockedNumbers = computed(() => Array.from(new Set(blockedNumbersText.value.split(/[\s,;]+/).map(value => value.replace(/\D/g, '')).filter(value => value.length >= 10))));
const statusLabel = computed(() => ({ off: 'Desligado', copilot: 'Copiloto', test: 'Teste controlado', live: 'Produção' })[draft.mode]);
const statusTone = computed(() => ({ off: 'neutral', copilot: 'blue', test: 'amber', live: 'green' })[draft.mode]);
const selectedProvider = computed(() => providers.value.find(item => item.id === draft.provider));
const selectedModel = computed(() => providerModels.value.find(item => item.id === draft.model));
const editableState = () => ({
  name: draft.name, mode: draft.mode, provider: draft.provider, model: draft.model,
  fallbackModel: draft.fallbackModel, monthlyBudget: Number(draft.monthlyBudget),
  maxReplyTokens: Number(draft.maxReplyTokens), temperature: Number(draft.temperature),
  allowedNumbers: parsedNumbers.value, blockedNumbers: parsedBlockedNumbers.value, systemPrompt: draft.systemPrompt, styleProfile: draft.styleProfile,
  tools: draft.tools, trainingLimit: Number(draft.trainingLimit),
});
const draftSignature = computed(() => JSON.stringify(editableState()));
const isDirty = computed(() => !!apiKey.value || draftSignature.value !== savedSignature.value);
const canSave = computed(() => !saving.value && !action.value && (isDirty.value || !draft.id));

const request = async (path, options = {}) => {
  const controller = new AbortController();
  requestControllers.add(controller);
  const timer = window.setTimeout(() => controller.abort(), options.timeout || 120000);
  try {
  const response = await fetch(`${API}${path}`, {
    method: options.method || 'GET',
    credentials: 'same-origin',
    headers: options.body ? { 'Content-Type': 'application/json' } : {},
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: controller.signal,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) throw new Error(payload.error || `Erro HTTP ${response.status}`);
  return payload;
  } finally {
    window.clearTimeout(timer);
    requestControllers.delete(controller);
  }
};

const loadProviders = async () => {
  const payload = await request('/api/providers');
  providers.value = payload.providers || [];
};

const loadN8nWorkflows = async () => {
  n8nLoading.value = true;
  try {
    const payload = await request(`/api/accounts/${accountId.value}/n8n/workflows`);
    n8nWorkflows.value = payload.workflows || [];
  } catch (error) {
    useAlert(`Não foi possível carregar as ferramentas do n8n: ${error.message}`);
  } finally { n8nLoading.value = false; }
};

const loadModels = async () => {
  if (!draft.provider) return;
  const generation = ++modelsGeneration;
  const context = `${accountId.value}:${selectedInboxId.value}:${draft.provider}`;
  const isCurrent = () => generation === modelsGeneration && context === `${accountId.value}:${selectedInboxId.value}:${draft.provider}`;
  modelsLoading.value = true;
  modelsError.value = '';
  try {
    const payload = await request(`/api/accounts/${accountId.value}/providers/${draft.provider}/models`, {
      method: 'POST',
      body: { apiKey: apiKey.value || undefined, configId: draft.id || undefined },
    });
    if (!isCurrent()) return;
    providerModels.value = payload.models || [];
    modelsFetchedAt.value = payload.fetchedAt || new Date().toISOString();
    if (!providerModels.value.some(item => item.id === draft.model)) {
      modelsError.value = 'O modelo salvo não aparece no catálogo atual. Escolha outro somente se desejar alterar a configuração.';
    }
  } catch (error) {
    if (!isCurrent()) return;
    providerModels.value = [];
    modelsFetchedAt.value = null;
    modelsError.value = error.message;
  } finally {
    if (isCurrent()) modelsLoading.value = false;
  }
};

const changeProvider = () => {
  apiKey.value = '';
  draft.hasApiKey = false;
  draft.model = selectedProvider.value?.defaultModel || '';
  draft.fallbackModel = '';
  providerTest.value = null;
  void loadModels();
};

const changeInbox = event => {
  const nextId = Number(event.target.value);
  if (!isDirty.value) { selectedInboxId.value = nextId; return; }
  pendingInboxId.value = nextId;
  showDiscardDialog.value = true;
  event.target.value = String(selectedInboxId.value);
};

const confirmInboxChange = () => {
  const nextId = pendingInboxId.value;
  showDiscardDialog.value = false;
  pendingInboxId.value = null;
  selectedInboxId.value = nextId;
};

const cancelInboxChange = () => {
  showDiscardDialog.value = false;
  pendingInboxId.value = null;
};

const resetDraft = inbox => {
  Object.assign(draft, {
    id: null,
    inboxId: inbox?.id || null,
    inboxName: inbox?.name || '',
    name: `Assistente ${inbox?.name || 'IA'}`,
    mode: 'off', provider: 'openrouter', model: 'z-ai/glm-5.3-flash', fallbackModel: 'google/gemini-2.5-flash-lite',
    monthlyBudget: 15, maxReplyTokens: 260, temperature: 0.35, allowedNumbers: [DEFAULT_TEST_NUMBER], blockedNumbers: [],
    systemPrompt: 'Atenda dúvidas gerais com objetividade. Consulte as ferramentas antes de afirmar dados de clientes, cobranças ou acessos. Transfira para um atendente quando não tiver certeza.',
    styleProfile: '', tools: defaultTools(), connected: false, hasApiKey: false, trainedAt: null, trainingLimit: 500, trainingConversations: 0,
    n8nWorkflowId: null, n8nWorkflowUrl: null, n8nSyncedAt: null, n8nToolCount: 0,
  });
  apiKey.value = '';
  allowedNumbersText.value = DEFAULT_TEST_NUMBER;
  blockedNumbersText.value = '';
  providerTest.value = null;
  stats.value = null;
};

const applyConfig = config => {
  resetDraft(selectedInbox.value);
  if (!config) {
    savedSignature.value = draftSignature.value;
    void loadModels();
    return;
  }
  Object.assign(draft, JSON.parse(JSON.stringify(config)));
  draft.tools = config.tools?.length ? JSON.parse(JSON.stringify(config.tools)) : defaultTools();
  allowedNumbersText.value = (config.allowedNumbers || []).join('\n');
  blockedNumbersText.value = (config.blockedNumbers || []).join('\n');
  savedSignature.value = draftSignature.value;
  void loadModels();
};

const loadConfigs = async () => {
  loading.value = true;
  try {
    const payload = await request(`/api/accounts/${accountId.value}/configs`);
    configs.value = payload.configs || [];
    if (!selectedInboxId.value && inboxes.value.length) selectedInboxId.value = inboxes.value[0].id;
    applyConfig(currentConfig.value);
  } catch (error) { useAlert(error.message); }
  finally { loading.value = false; }
};

watch(selectedInboxId, () => applyConfig(currentConfig.value));
watch([() => draft.provider, () => draft.model, apiKey], () => { providerTest.value = null; });

const save = async ({ quiet = false, syncN8n = true } = {}) => {
  if (saving.value) throw new Error('Aguarde a configuração terminar de salvar.');
  if (!selectedInbox.value) throw new Error('Selecione uma inbox.');
  const context = `${accountId.value}:${selectedInboxId.value}`;
  saving.value = true;
  try {
    const payload = await request(`/api/accounts/${accountId.value}/configs`, {
      method: 'POST',
      body: {
        ...draft,
        inboxId: selectedInbox.value.id,
        inboxName: selectedInbox.value.name,
        apiKey: apiKey.value || undefined,
        allowedNumbers: parsedNumbers.value,
        blockedNumbers: parsedBlockedNumbers.value,
        trainingLimit: Math.min(500, Math.max(10, Number(draft.trainingLimit || 500))),
        syncN8n,
      },
    });
    const index = configs.value.findIndex(item => Number(item.inboxId) === Number(payload.config.inboxId));
    if (index >= 0) configs.value[index] = payload.config; else configs.value.push(payload.config);
    if (context === `${accountId.value}:${selectedInboxId.value}`) applyConfig(payload.config);
    if (payload.syncError) useAlert(payload.syncError);
    else if (!quiet) useAlert(payload.n8nSynced ? 'Configuração salva e workflow do n8n atualizado.' : 'Configuração salva com segurança.');
    return payload.config;
  } catch (error) { useAlert(error.message); throw error; }
  finally { saving.value = false; }
};

const runAction = async (name, callback) => {
  if (action.value || saving.value) return;
  action.value = name;
  try { await callback(); }
  catch (error) { useAlert(error.message); }
  finally { action.value = ''; }
};

const testProvider = () => runAction('test', async () => {
  providerTest.value = await request(`/api/accounts/${accountId.value}/providers/${draft.provider}/test`, {
    method: 'POST',
    body: { apiKey: apiKey.value || undefined, configId: draft.id || undefined, model: draft.model },
  });
  useAlert('Conexão com o modelo confirmada.');
});

const train = () => runAction('train', async () => {
  const config = await save({ quiet: true });
  const payload = await request(`/api/accounts/${accountId.value}/configs/${config.id}/train`, { method: 'POST', body: { limit: config.trainingLimit } });
  useAlert(`${payload.trained.conversations} conversas analisadas e anonimizadas.`);
  await loadConfigs();
  activeTab.value = 'knowledge';
});

const connect = () => runAction('connect', async () => {
  const config = await save({ quiet: true, syncN8n: false });
  const payload = await request(`/api/accounts/${accountId.value}/configs/${config.id}/provision`, { method: 'POST' });
  const index = configs.value.findIndex(item => item.id === payload.config.id);
  if (index >= 0) configs.value[index] = payload.config;
  applyConfig(payload.config);
  useAlert('AI Agent criado no n8n e conectado ao Chatwoot. O modo de segurança permanece ativo.');
});

const disconnect = () => runAction('disconnect', async () => {
  const payload = await request(`/api/accounts/${accountId.value}/configs/${draft.id}/disconnect`, { method: 'POST' });
  const index = configs.value.findIndex(item => item.id === payload.config.id);
  if (index >= 0) configs.value[index] = payload.config;
  applyConfig(payload.config);
  useAlert('Chatbot desligado e desconectado da inbox.');
});

const addKnowledge = () => runAction('knowledge', async () => {
  const config = await save({ quiet: true });
  await request(`/api/accounts/${accountId.value}/configs/${config.id}/knowledge`, { method: 'POST', body: { title: knowledgeTitle.value, content: knowledgeContent.value } });
  knowledgeTitle.value = '';
  knowledgeContent.value = '';
  useAlert('Conhecimento adicionado ao RAG.');
  await loadStats();
});

const addTool = () => {
  if (draft.tools.length >= 12) { useAlert('O limite é de 12 ferramentas por chatbot.'); return; }
  draft.tools.push({ name: `nova_ferramenta_${draft.tools.length + 1}`, description: '', n8nWorkflowId: '', enabled: false, requiresApproval: true });
};

const removeTool = index => { draft.tools.splice(index, 1); };

const loadStats = async () => {
  const generation = ++statsGeneration;
  const configId = draft.id;
  if (!configId) { useAlert('Salve a configuração para consultar a atividade.'); return; }
  try {
    const payload = await request(`/api/accounts/${accountId.value}/configs/${configId}/stats`);
    if (generation === statsGeneration && draft.id === configId) stats.value = payload;
  } catch (error) { if (generation === statsGeneration) useAlert(error.message); }
};

const selectMode = mode => {
  if (mode === 'live' && (!draft.connected || !draft.trainedAt)) {
    useAlert('Conecte o Agent Bot e treine a base antes de liberar a produção.');
    return;
  }
  draft.mode = mode;
  if (mode === 'live') useAlert('Modo Produção selecionado. Revise as configurações e clique em Salvar para confirmar.');
};

const beforeUnload = event => {
  if (!isDirty.value) return;
  event.preventDefault();
  event.returnValue = '';
};

onMounted(async () => {
  if (!isAdmin.value) { loading.value = false; return; }
  try {
  await Promise.all([store.dispatch('inboxes/get'), loadProviders(), loadN8nWorkflows()]);
  await loadConfigs();
  } catch (error) { useAlert(error.message); }
  window.addEventListener('beforeunload', beforeUnload);
});
onBeforeUnmount(() => {
  ++modelsGeneration;
  ++statsGeneration;
  requestControllers.forEach(controller => controller.abort());
  requestControllers.clear();
  window.removeEventListener('beforeunload', beforeUnload);
});
</script>

<template>
  <main class="flex h-full w-full min-w-0 flex-col overflow-hidden bg-n-slate-1 text-n-slate-12">
    <header class="flex flex-col gap-4 border-b border-n-weak bg-n-background px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between">
      <div class="flex min-w-0 items-center gap-3">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand">
          <i class="i-lucide-bot size-5" />
        </div>
        <div class="min-w-0">
          <h1 class="truncate text-lg font-semibold text-n-slate-12">Chatbot IA</h1>
          <p class="mt-0.5 text-sm text-n-slate-10">Configure, teste e acompanhe o assistente da inbox selecionada.</p>
        </div>
      </div>

      <div class="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end md:w-auto md:flex-nowrap">
        <label class="min-w-0 flex-1 md:w-56 md:flex-none">
          <span class="mb-1 block text-xs font-medium text-n-slate-10">Inbox / instância</span>
          <select :value="selectedInboxId" class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20" aria-label="Selecionar inbox" @change="changeInbox">
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">{{ inbox.name }}</option>
          </select>
        </label>
        <div v-if="selectedInbox" class="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:pb-0.5">
          <span class="whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs font-medium" :class="{
            'bg-n-slate-3 text-n-slate-11': statusTone === 'neutral',
            'bg-n-blue-3 text-n-blue-11': statusTone === 'blue',
            'bg-n-amber-3 text-n-amber-11': statusTone === 'amber',
            'bg-n-teal-3 text-n-teal-11': statusTone === 'green',
          }">{{ statusLabel }}</span>
          <button v-if="draft.connected" class="h-9 whitespace-nowrap rounded-lg border border-n-weak px-3 text-sm hover:bg-n-alpha-2 disabled:opacity-50" :disabled="!!action" @click="disconnect">Desconectar</button>
          <span v-if="isDirty" class="text-xs font-medium text-n-amber-11">Alterações não salvas</span>
          <button class="h-9 whitespace-nowrap rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50" :disabled="!canSave" @click="save()">{{ saving ? 'Salvando…' : 'Salvar' }}</button>
        </div>
      </div>
    </header>
    <div v-if="!isAdmin" class="m-6 rounded-lg border border-n-weak bg-n-background p-5 text-sm text-n-slate-11">
      As configurações do Chatbot IA ficam disponíveis apenas para administradores. O atendimento continua disponível normalmente nas conversas.
    </div>
    <template v-else>

    <div v-if="loading" class="flex flex-1 items-center justify-center text-sm text-n-slate-10">Carregando configurações…</div>
    <div v-else-if="!selectedInbox" class="flex flex-1 items-center justify-center p-6 text-center text-sm text-n-slate-10">Nenhuma inbox disponível.</div>
    <section v-else class="flex-1 overflow-y-auto p-4 sm:p-6">
      <div class="w-full">
        <div class="mb-5 flex gap-1 overflow-x-auto border-b border-n-weak">

          <button v-for="tab in [{id:'setup',label:'Configuração'},{id:'behavior',label:'Comportamento'},{id:'knowledge',label:'Conhecimento'},{id:'tools',label:'Ferramentas'},{id:'activity',label:'Atividade'}]" :key="tab.id" class="whitespace-nowrap border-b-2 px-3 py-2 text-sm" :class="activeTab === tab.id ? 'border-n-brand text-n-blue-text' : 'border-transparent text-n-slate-10 hover:text-n-slate-12'" @click="activeTab = tab.id; tab.id === 'activity' && loadStats()">{{ tab.label }}</button>
        </div>

        <section v-if="activeTab === 'setup'" class="space-y-5">
          <div class="rounded-lg border border-n-weak bg-n-background p-4">
            <h3 class="text-base font-semibold">1. Modo de operação</h3>
            <p class="mb-4 mt-1 text-sm text-n-slate-10">Comece em Copiloto ou Teste. Produção só é liberada após conexão e treinamento.</p>
            <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <button v-for="mode in [{id:'off',name:'Desligado',hint:'Não processa mensagens'},{id:'copilot',name:'Copiloto',hint:'Sugere em nota privada'},{id:'test',name:'Teste',hint:'Só números permitidos'},{id:'live',name:'Produção',hint:'Responde ao público'}]" :key="mode.id" class="rounded-lg border p-3 text-left" :class="draft.mode === mode.id ? 'border-n-brand bg-n-brand/5' : 'border-n-weak hover:bg-n-alpha-1'" @click="selectMode(mode.id)">
                <span class="block text-sm font-medium">{{ mode.name }}</span><span class="mt-1 block text-xs text-n-slate-10">{{ mode.hint }}</span>
              </button>
            </div>
          </div>

          <div v-if="draft.mode === 'test'" class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-4">
            <label class="text-sm font-semibold">Números autorizados para teste</label>
            <p class="mb-2 mt-1 text-xs text-n-slate-10">Um por linha, com DDI e DDD. Somente esses contatos recebem respostas automáticas pelo WhatsApp.</p>
            <textarea v-model="allowedNumbersText" rows="3" class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm" placeholder="5511999999999" />
          </div>

          <div class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-4">
            <label class="text-sm font-semibold">Blacklist: atendimento humano</label>
            <p class="mb-2 mt-1 text-xs text-n-slate-10">Um por linha, com DDI e DDD. Estes números nunca recebem resposta automática, inclusive em Produção.</p>
            <textarea v-model="blockedNumbersText" rows="3" class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm" placeholder="5511999999999" />
          </div>

          <div class="grid gap-5 lg:grid-cols-2">
            <div class="rounded-lg border border-n-weak bg-n-background p-4">
              <h3 class="mb-4 text-base font-semibold">2. Modelo</h3>
              <label class="field-label">Provedor</label>
              <select v-model="draft.provider" class="field" @change="changeProvider">
                <option v-for="provider in providers" :key="provider.id" :value="provider.id">{{ provider.name }}</option>
              </select>
              <p v-if="selectedProvider" class="-mt-2 mb-4 text-xs text-n-slate-10">{{ selectedProvider.description }}</p>
              <label class="field-label">Modelo principal</label>
              <select v-model="draft.model" class="field" :disabled="modelsLoading || !providerModels.length">
                <option v-if="modelsLoading" value="">Atualizando modelos…</option>
                <option v-for="model in providerModels" :key="model.id" :value="model.id">{{ model.name }}{{ model.contextLength ? ` · ${Number(model.contextLength).toLocaleString('pt-BR')} tokens` : '' }}</option>
              </select>
              <p v-if="selectedModel" class="-mt-2 mb-4 text-xs text-n-slate-10">
                Contexto: {{ selectedModel.contextLength ? Number(selectedModel.contextLength).toLocaleString('pt-BR') + ' tokens' : 'não informado' }}
                <template v-if="selectedModel.inputPricePerMillion || selectedModel.outputPricePerMillion"> · Entrada US$ {{ Number(selectedModel.inputPricePerMillion || 0).toFixed(2) }}/M · Saída US$ {{ Number(selectedModel.outputPricePerMillion || 0).toFixed(2) }}/M</template>
              </p>
              <label class="field-label">Modelo reserva</label>
              <select v-model="draft.fallbackModel" class="field" :disabled="modelsLoading || !providerModels.length">
                <option value="">Sem modelo reserva</option>
                <option v-for="model in providerModels" :key="model.id" :value="model.id" :disabled="model.id === draft.model">{{ model.name }}</option>
              </select>
              <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-n-slate-10">
                <span v-if="modelsFetchedAt">Catálogo atualizado em {{ new Date(modelsFetchedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}.</span>
                <span v-else-if="modelsError" class="text-n-ruby-11">{{ modelsError }}</span>
                <button type="button" class="font-medium text-n-blue-text hover:underline disabled:opacity-50" :disabled="modelsLoading" @click="loadModels">{{ modelsLoading ? 'Atualizando…' : 'Atualizar modelos' }}</button>
              </div>
            </div>
            <div class="rounded-lg border border-n-weak bg-n-background p-4">
              <h3 class="mb-1 text-base font-semibold">3. Conexão segura</h3>
              <p class="mb-4 text-xs leading-5 text-n-slate-10">A chave é criptografada no servidor e nunca volta para o navegador.</p>
              <label class="field-label">API key</label>
              <input v-model="apiKey" type="password" autocomplete="new-password" class="field" :placeholder="draft.hasApiKey ? 'Chave já configurada — preencha apenas para trocar' : 'Cole sua chave aqui'">
              <button class="mb-2 w-full rounded-lg border border-n-weak px-3 py-2 text-sm font-medium hover:bg-n-alpha-2 disabled:opacity-50" :disabled="modelsLoading || (!apiKey && !draft.hasApiKey)" @click="loadModels">{{ modelsLoading ? 'Buscando modelos…' : 'Buscar modelos do provedor' }}</button>
              <button class="w-full rounded-lg border border-n-brand px-3 py-2 text-sm font-medium text-n-blue-text hover:bg-n-brand/5 disabled:opacity-50" :disabled="action === 'test' || (!apiKey && !draft.hasApiKey) || !draft.model" @click="testProvider">{{ action === 'test' ? 'Testando…' : 'Testar conexão sem salvar' }}</button>
              <div v-if="providerTest" class="mt-3 rounded-lg bg-n-teal-3 p-3 text-xs text-n-teal-11">Conectado a {{ providerTest.model }} em {{ providerTest.latencyMs }} ms.</div>
            </div>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-background p-4">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 class="text-base font-semibold">4. Criar e conectar no n8n</h3>
                <p class="mt-1 max-w-2xl text-sm text-n-slate-10">Cria um workflow visual com AI Agent, memória e ferramentas; depois conecta o Agent Bot à inbox. Após conectado, todas as alterações salvas são sincronizadas automaticamente.</p>
              </div>
              <span v-if="draft.n8nSyncedAt" class="rounded-full bg-n-teal-3 px-2.5 py-1 text-xs font-medium text-n-teal-11">Sincronizado</span>
            </div>
            <div class="mt-4 flex flex-wrap gap-2">
              <button class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50" :disabled="!safeToConnect || !!action" @click="connect">{{ action === 'connect' ? 'Validando conexão…' : draft.connected ? 'Validar e sincronizar novamente' : 'Criar workflow e conectar' }}</button>
              <a v-if="draft.n8nWorkflowUrl" :href="draft.n8nWorkflowUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium hover:bg-n-alpha-2"><i class="i-lucide-workflow size-4" /> Abrir workflow no n8n</a>
            </div>
            <p v-if="draft.n8nSyncedAt" class="mt-3 text-xs text-n-slate-10">Última sincronização: {{ new Date(draft.n8nSyncedAt).toLocaleString('pt-BR') }} · {{ draft.n8nToolCount }} ferramenta(s) conectada(s).</p>
          </div>
        </section>

        <section v-else-if="activeTab === 'behavior'" class="space-y-5">
          <div class="rounded-lg border border-n-weak bg-n-background p-4">
            <label class="field-label">Nome exibido</label><input v-model="draft.name" class="field">
            <label class="field-label">Instruções da empresa</label>
            <textarea v-model="draft.systemPrompt" rows="8" class="field" placeholder="Explique o que a IA pode fazer, quando deve transferir e quais regras deve seguir." />
            <label class="field-label">Perfil de linguagem</label>
            <textarea v-model="draft.styleProfile" rows="5" class="field" placeholder="Será preenchido automaticamente ao analisar as conversas, mas você pode ajustar." />
          </div>
          <div class="grid gap-5 md:grid-cols-3">
            <label class="metric-field"><span>Limite mensal estimado</span><div><span>US$</span><input v-model.number="draft.monthlyBudget" type="number" min="1" step="1"></div><small class="mt-2 block text-xs font-normal text-n-slate-10">Quando o provedor não informa o custo, usamos uma estimativa conservadora.</small></label>
            <label class="metric-field"><span>Máximo da resposta</span><div><input v-model.number="draft.maxReplyTokens" type="number" min="80" max="800"><span>tokens</span></div></label>
            <label class="metric-field"><span>Criatividade</span><div><input v-model.number="draft.temperature" type="number" min="0" max="1" step="0.05"><span>0–1</span></div></label>
          </div>
        </section>

        <section v-else-if="activeTab === 'knowledge'" class="space-y-5">
          <div class="rounded-lg border border-n-weak bg-n-background p-4">
            <div class="flex flex-wrap items-end justify-between gap-3">
              <div><h3 class="text-base font-semibold">Aprender com os atendimentos</h3><p class="mt-1 max-w-2xl text-sm text-n-slate-10">Analisa as conversas resolvidas mais recentes, mascara identificadores comuns como telefone, CPF e e-mail e cria exemplos para o RAG. Revise a base antes da produção; o histórico original não é alterado.</p></div>
              <div class="flex flex-wrap items-end gap-2">
                <label class="w-40"><span class="field-label">Quantidade</span><input v-model.number="draft.trainingLimit" type="number" min="10" max="500" step="10" class="field !mb-0" aria-label="Quantidade de conversas para treinamento"></label>
                <button class="h-10 rounded-lg bg-n-brand px-4 text-sm font-medium text-white disabled:opacity-50" :disabled="!draft.id || action === 'train'" @click="train">{{ action === 'train' ? 'Analisando…' : `Analisar ${Math.min(500, Math.max(10, Number(draft.trainingLimit || 500)))} conversas` }}</button>
              </div>
            </div>
            <div v-if="draft.trainedAt" class="mt-4 rounded-lg bg-n-teal-3 p-3 text-sm text-n-teal-11">{{ draft.trainingConversations }} conversas processadas. Base atualizada em {{ new Date(draft.trainedAt).toLocaleString('pt-BR') }}.</div>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-background p-4">
            <h3 class="text-base font-semibold">Adicionar conhecimento confiável</h3><p class="mb-4 mt-1 text-sm text-n-slate-10">Use para preços, políticas, procedimentos e informações que devem prevalecer sobre exemplos antigos.</p>
            <label class="field-label">Título</label><input v-model="knowledgeTitle" class="field" placeholder="Ex: Política de renovação">
            <label class="field-label">Conteúdo</label><textarea v-model="knowledgeContent" rows="7" class="field" placeholder="Cole aqui o conteúdo revisado…" />
            <button class="rounded-lg border border-n-brand px-4 py-2 text-sm font-medium text-n-blue-text disabled:opacity-50" :disabled="knowledgeContent.length < 20 || !!action" @click="addKnowledge">Adicionar ao RAG</button>
          </div>
        </section>

        <section v-else-if="activeTab === 'tools'" class="space-y-3">
          <div class="mb-5 rounded-lg border border-n-weak bg-n-background p-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div><h3 class="text-sm font-semibold">Tools do AI Agent</h3><p class="mt-1 text-xs leading-5 text-n-slate-10">Escolha workflows que começam com “When Executed by Another Workflow”. Eles aparecerão conectados diretamente ao AI Agent no n8n.</p></div>
              <div class="flex items-center gap-3"><button type="button" class="text-xs font-medium text-n-blue-text hover:underline disabled:opacity-50" :disabled="n8nLoading" @click="loadN8nWorkflows">{{ n8nLoading ? 'Atualizando…' : 'Atualizar lista' }}</button><button type="button" class="rounded-lg border border-n-weak px-3 py-2 text-xs font-medium hover:bg-n-alpha-2" @click="addTool">Adicionar ferramenta</button></div>
            </div>
          </div>
          <div class="mb-5 rounded-lg border border-n-amber-6 bg-n-amber-2 p-4 text-sm text-n-amber-11">Ferramentas marcadas para atendimento humano nunca são executadas automaticamente. O chatbot explica o encaminhamento e transfere a conversa. Em bots conectados, basta salvar para sincronizar as alterações.</div>
          <div v-for="(tool, index) in draft.tools" :key="`${tool.name}-${index}`" class="rounded-lg border border-n-weak bg-n-background p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="grid min-w-0 flex-1 gap-3 sm:grid-cols-2"><label><span class="field-label">Nome técnico</span><input v-model="tool.name" class="field !mb-0" placeholder="consultar_cliente"></label><label><span class="field-label">Descrição para a IA</span><input v-model="tool.description" class="field !mb-0" placeholder="O que esta ferramenta faz"></label></div>
              <div class="flex items-center gap-3"><label class="flex items-center gap-2 text-sm"><input v-model="tool.enabled" type="checkbox"> Ativa</label><button type="button" class="text-xs text-n-ruby-11 hover:underline" :aria-label="`Remover ${tool.name}`" @click="removeTool(index)">Remover</button></div>
            </div>
            <div v-if="tool.enabled" class="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
              <label class="min-w-0"><span class="field-label">Workflow do n8n</span><select v-model="tool.n8nWorkflowId" class="field !mb-0"><option value="">Selecione um workflow…</option><option v-for="workflow in n8nWorkflows" :key="workflow.id" :value="workflow.id">{{ workflow.name }}</option></select></label>
              <label class="mt-5 flex min-h-10 items-center gap-2 rounded-lg border border-n-weak px-3 text-xs" title="A tool não será executada automaticamente; a conversa será encaminhada para um atendente."><input v-model="tool.requiresApproval" type="checkbox"> Exigir atendente humano</label>
            </div>
          </div>
        </section>

        <section v-else class="space-y-5">
          <div v-if="!stats" class="py-12 text-center text-sm text-n-slate-10">Carregando atividade…</div>
          <template v-else>
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div class="stat-card"><span>Respostas</span><strong>{{ stats.summary.sent }}</strong></div>
              <div class="stat-card"><span>Falhas</span><strong>{{ stats.summary.failed }}</strong></div>
              <div class="stat-card"><span>Tokens no mês</span><strong>{{ Number(stats.summary.tokens).toLocaleString('pt-BR') }}</strong></div>
              <div class="stat-card"><span>Custo no mês</span><strong>US$ {{ Number(stats.summary.cost).toFixed(3) }}</strong></div>
            </div>
            <div class="overflow-hidden rounded-lg border border-n-weak bg-n-background">
              <div class="border-b border-n-weak p-4"><h3 class="text-sm font-semibold">Últimas execuções</h3></div>
              <div v-if="!stats.runs.length" class="p-8 text-center text-sm text-n-slate-10">Ainda não há execuções.</div>
              <div v-for="run in stats.runs" :key="run.id" class="grid gap-2 border-b border-n-weak px-4 py-3 text-xs last:border-0 md:grid-cols-[100px_1fr_1fr_90px]">
                <span :class="run.status === 'sent' ? 'text-n-teal-11' : 'text-n-ruby-11'">{{ run.status === 'sent' ? 'Enviado' : 'Falhou' }}</span><span class="truncate text-n-slate-10">{{ run.input_preview || run.error }}</span><span class="truncate">{{ run.output_preview || '—' }}</span><span class="text-right text-n-slate-10">{{ run.latency_ms ? `${run.latency_ms} ms` : '—' }}</span>
              </div>
            </div>
          </template>
        </section>

      </div>
    </section>
    </template>

    <div v-if="showDiscardDialog" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="discard-title" @keydown.esc="cancelInboxChange">
      <div class="w-full max-w-md rounded-xl border border-n-weak bg-n-background p-5 shadow-xl"><h2 id="discard-title" class="text-base font-semibold">Descartar alterações?</h2><p class="mt-2 text-sm text-n-slate-10">Você alterou esta configuração. Salve antes de trocar de inbox ou descarte as mudanças.</p><div class="mt-5 flex justify-end gap-2"><button class="rounded-lg border border-n-weak px-4 py-2 text-sm" @click="cancelInboxChange">Continuar editando</button><button class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white" @click="confirmInboxChange">Descartar e trocar</button></div></div>
    </div>
  </main>
</template>

<style scoped>
.field-label { @apply mb-1.5 block text-xs font-medium text-n-slate-11; }
.field { @apply mb-4 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-brand; }
.metric-field { @apply rounded-lg border border-n-weak bg-n-background p-4; }
.metric-field > span { @apply mb-2 block text-xs font-medium text-n-slate-10; }
.metric-field > div { @apply flex items-center gap-2; }
.metric-field input { @apply min-w-0 flex-1 bg-transparent text-lg font-semibold outline-none; }
.metric-field div span { @apply text-xs text-n-slate-10; }
.stat-card { @apply rounded-lg border border-n-weak bg-n-background p-4; }
.stat-card span { @apply block text-xs text-n-slate-10; }
.stat-card strong { @apply mt-1 block text-xl font-semibold; }
</style>
